import React, { type ReactNode } from "react";

import clsx from "clsx";
import { twMerge } from "tailwind-merge";

import {
  GenericTooltip,
  type GenericTooltipProps,
} from "@components/tooltip-container";

interface LearnMoreTooltipProps
  extends Pick<
    GenericTooltipProps,
    | "overlayClassName"
    | "infoCircleClassName"
    | "noInfoCircle"
    | "showTooltip"
    | "placement"
  > {
  title: string;
  content: string | ReactNode;
  link?: string;
  linkLabel?: string;
  darkToolTip?: boolean;
  className?: string;
  contentClassName?: string;
  children?: React.ReactNode;
}

export const LearnMoreTooltip = (props: LearnMoreTooltipProps) => {
  const {
    title,
    content,
    link,
    overlayClassName,
    darkToolTip,
    linkLabel = "Click here to learn more",
    infoCircleClassName,
    children,
    className,
    placement,
    noInfoCircle = false,
    showTooltip = true,
    contentClassName,
  } = props;

  return (
    <GenericTooltip
      className={className}
      infoCircleClassName={infoCircleClassName}
      overlayClassName={overlayClassName}
      hideArrow={!!children}
      noInfoCircle={noInfoCircle}
      showTooltip={showTooltip}
      placement={placement}
      content={
        <div
          className={twMerge("flex flex-col space-y-2 w-64", contentClassName)}
        >
          <div
            className={clsx("font-bold tracking-wide", {
              "text-white": darkToolTip,
              "text-black": !darkToolTip,
            })}
          >
            {title}
          </div>
          <div
            className={clsx({
              "text-white": darkToolTip,
              "text-black": !darkToolTip,
            })}
          >
            {content}
          </div>
          {link && (
            <a
              rel="noreferrer"
              href={link}
              target="_blank"
              className={clsx(
                "-ml-2 bottom-0 no-underline hover:bg-cosmic-4",
                "hover:text-cosmic rounded-full py-1 px-2 font-medium w-fit max-w-full",
              )}
            >
              {linkLabel}
            </a>
          )}
        </div>
      }
    >
      {children}
    </GenericTooltip>
  );
};
